import {
  Alert,
  Box,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import { getProjectConfig, setProjectConfig } from '../../../modules/executions';
import {
  browserOptions,
  cloudDeviceLabs,
  platforms,
  subPlatformOptions,
} from '../../../static-data/configDropdownOptions';
import Loader from '../../elements/Loader';
import ActionButton from '../../../ui/primitives/ActionButton';
import MultiSelectDropdown from '../../../ui/primitives/MultiSelectDropdown';
import ConfirmationModal from '../../../ui/primitives/ConfirmationModal';
import CustomInputField from '../../../ui/primitives/CustomInputField';
import CustomSelect from '../../../ui/primitives/CustomSelect';
import CountrySelect from './Countries';
import LanguageSelect from './Languages';
import useExecutionScope from '../../../hooks/useExecutionScope';
import { Stack } from '@mui/material';

const defaultFormData = {
  geographies: [],
  languages: [],
  cloud_device: [],
  platforms: [],
  sub_platforms: [],
  browsers: [],
  other_cloud_device: '',
  other_browsers: '',
  multiple_geographies: 'No',
  multiple_languages: 'No',
};

const yesNoOptions = [
  { id: 'Yes', name: 'Yes' },
  { id: 'No', name: 'No' },
];

const StaticField = ({ label, value }) => (
  <div>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    <Typography variant="body1" className="form-static-text">
      {value || 'None'}
    </Typography>
  </div>
);

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
    {children}
  </Typography>
);

const toOptions = (arr = []) => arr.map((s) => ({ id: s, name: s }));

const ExecutionScope = ({ isAdmin }) => {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState(defaultFormData);
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { addNotification } = useNotification();
  const { isLoading, isPlatformsDefined } = useExecutionScope(projectId);

  const setFormData = (config) => {
    setFormState({
      geographies: config?.geographies || [],
      languages: config?.languages || [],
      cloud_device: config?.cloud_device || [],
      platforms: config?.platforms || [],
      sub_platforms: config?.sub_platforms || [],
      browsers: config?.browsers || [],
      other_cloud_device: config?.other_cloud_device || '',
      other_browsers: Array.isArray(config?.other_browsers)
        ? config.other_browsers.join(', ')
        : config?.other_browsers || '',
      multiple_geographies: config?.multiple_geographies || 'No',
      multiple_languages: config?.multiple_languages || 'No',
    });
  };

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const response = await getProjectConfig(projectId);
      setFormData(response);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const handleChange = (name, value) => {
    if (name === 'platforms') {
      const filteredSubPlatforms = getFilteredSubPlatforms(value);
      setFormState((prevState) => ({
        ...prevState,
        platforms: value,
        sub_platforms:
          filteredSubPlatforms?.length > 0
            ? prevState.sub_platforms.filter((sub) =>
              filteredSubPlatforms.find((f) => f.name === sub)
            )
            : [],
      }));
    } else {
      setFormState((prevState) => ({ ...prevState, [name]: value }));
    }
  };

  const handleSave = async () => {
    setShowConfirmModal(false);
    setSubmitting(true);
    try {
      await setProjectConfig(projectId, formState);
      addNotification('success', 'Configurations updated successfully!');
      await fetchProjectData();
      setIsEditing(false);
    } catch (error) {
      addNotification('error', 'Failed to update configurations');
    }
    setSubmitting(false);
  };

  const getFilteredSubPlatforms = (selectedPlatforms) => {
    const isWebSelected = selectedPlatforms.includes('Web');
    const isMobileSelected =
      selectedPlatforms.includes('Mobile Responsive') ||
      selectedPlatforms.includes('Native Mobile App') ||
      selectedPlatforms.includes('Hybrid Mobile App');
    return subPlatformOptions.filter(
      (option) =>
        (option.category === 'Web' && isWebSelected) ||
        (option.category === 'Mobile' && isMobileSelected)
    );
  };

  const getFilteredSubPlatformOptions = () => getFilteredSubPlatforms(formState.platforms).map((o) => o.name);

  const showSubplatforms =
    formState.platforms.includes('Web') ||
    formState.platforms.some((p) =>
      ['Mobile Responsive', 'Native Mobile App', 'Hybrid Mobile App'].includes(p)
    );

  const availableBrowserOptions = browserOptions.map((b) =>
    typeof b === 'string' ? b : b.name
  );

  if (loading) return <Loader />;

  return (
    <Box>
      <ConfirmationModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleSave}
        title="Save Changes"
        body={
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Some configurations have been modified.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Removed values will be automatically removed from existing{' '}
              <strong>Test Suites</strong>, <strong>Test Cases</strong>,{' '}
              <strong>Test Cycles</strong>, and <strong>Iterations</strong> that currently use them.
            </Typography>
            <Typography variant="body2" color="warning.main">
              This action cannot be undone. Please review your changes before saving.
            </Typography>
          </Box>
        }
        buttonLabel="Confirm & Save"
        buttonStyle="primary"
        loading={submitting}
      />

      {!isLoading && !isPlatformsDefined && formState?.platforms?.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No testing platforms have been selected. Specifying them will improve testing coverage
          and efficiency.
        </Alert>
      )}

      {/* Testing Platforms */}
      <SectionTitle>Testing Platforms</SectionTitle>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          {isEditing ? (
            <MultiSelectDropdown
              label="Platform"
              name="platforms"
              options={toOptions(platforms.map((p) => (typeof p === 'string' ? p : p.name)))}
              selectedValues={formState.platforms}
              onChange={(value) => handleChange('platforms', value)}
              placeholder="Select platforms"
              showSelectAll
            />
          ) : (
            <StaticField label="Platform" value={formState.platforms?.join(', ')} />
          )}
        </Grid>

        {showSubplatforms && (
          <Grid item xs={12} md={4}>
            {isEditing ? (
              <MultiSelectDropdown
                label="Supported Options"
                name="sub_platforms"
                options={toOptions(getFilteredSubPlatformOptions())}
                selectedValues={formState.sub_platforms || []}
                onChange={(value) => handleChange('sub_platforms', value)}
                placeholder="Select options"
                showSelectAll
              />
            ) : (
              <StaticField
                label="Supported Options"
                value={formState.sub_platforms?.join(', ')}
              />
            )}
          </Grid>
        )}

        {showSubplatforms && (
          <Grid item xs={12} md={4}>
            {isEditing ? (
              <MultiSelectDropdown
                label="Browsers"
                name="browsers"
                options={toOptions(availableBrowserOptions)}
                selectedValues={formState.browsers}
                onChange={(value) => handleChange('browsers', value)}
                placeholder="Select browsers"
                showSelectAll
              />
            ) : (
              <StaticField
                label="Browsers"
                value={[
                  ...formState.browsers.filter((b) => b !== 'Others'),
                  ...(formState.browsers.includes('Others') && formState.other_browsers
                    ? [`Others (${formState.other_browsers})`]
                    : []),
                ].join(', ')}
              />
            )}
          </Grid>
        )}

        {isEditing && showSubplatforms && formState.browsers.includes('Others') && (
          <Grid item xs={12} md={4}>
            <CustomInputField
              value={formState.other_browsers}
              onChange={(value) => handleChange('other_browsers', value)}
              fullWidth
              label="Specify Other Browsers (Comma-Separated)"
            />
          </Grid>
        )}
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Language & Geography */}
      <SectionTitle>Language & Geography</SectionTitle>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          {isEditing ? (
            <CustomSelect
              label="Support multiple languages?"
              name="multiple_languages"
              options={yesNoOptions}
              value={formState.multiple_languages ?? 'No'}
              onChange={(value) => handleChange('multiple_languages', value)}
              placeholder="Select"
            />
          ) : (
            <StaticField
              label="Multiple Languages"
              value={
                formState.multiple_languages === 'Yes'
                  ? `Yes, ${formState.languages?.join(', ') || ''}`
                  : 'No'
              }
            />
          )}
        </Grid>

        {isEditing && formState.multiple_languages === 'Yes' && (
          <Grid item xs={12} md={8}>
            <LanguageSelect
              selectedValues={formState.languages}
              setSelectedValues={(v) => setFormState({ ...formState, languages: v })}
            />
          </Grid>
        )}

        <Grid item xs={12} md={4}>
          {isEditing ? (
            <CustomSelect
              label="Support multiple geographies?"
              name="multiple_geographies"
              options={yesNoOptions}
              value={formState.multiple_geographies ?? 'No'}
              onChange={(value) => handleChange('multiple_geographies', value)}
              placeholder="Select"
            />
          ) : (
            <StaticField
              label="Multiple Geographies"
              value={
                formState.multiple_geographies === 'Yes'
                  ? `Yes, ${formState.geographies?.join(', ') || ''}`
                  : 'No'
              }
            />
          )}
        </Grid>

        {isEditing && formState.multiple_geographies === 'Yes' && (
          <Grid item xs={12} md={8}>
            <CountrySelect
              selectedValues={formState.geographies}
              setSelectedValues={(v) => setFormState({ ...formState, geographies: v })}
            />
          </Grid>
        )}
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Cloud Device Labs */}
      <SectionTitle>Cloud Device Labs</SectionTitle>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          {isEditing ? (
            <MultiSelectDropdown
              label="Cloud Device Labs"
              name="cloud_device"
              options={toOptions(
                cloudDeviceLabs.map((c) => (typeof c === 'string' ? c : c.name))
              )}
              selectedValues={formState.cloud_device}
              onChange={(value) => handleChange('cloud_device', value)}
              placeholder="Select cloud labs"
              showSelectAll
            />
          ) : (
            <StaticField
              label="Cloud Device Labs"
              value={[
                ...formState.cloud_device.filter((d) => d !== 'Others'),
                ...(formState.cloud_device.includes('Others') && formState.other_cloud_device
                  ? [`Others (${formState.other_cloud_device})`]
                  : []),
              ].join(', ')}
            />
          )}
        </Grid>

        {isEditing && formState.cloud_device.includes('Others') && (
          <Grid item xs={12} md={4}>
            <CustomInputField
              value={formState.other_cloud_device}
              onChange={(value) => handleChange('other_cloud_device', value)}
              fullWidth
              label="Specify Other Cloud Device Lab"
            />
          </Grid>
        )}
      </Grid>

      {isAdmin && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
          {isEditing ? (
            <Stack direction="row" spacing={2}>
              <ActionButton
                label="Cancel"
                intent="tertiary"
                onClick={() => {
                  setIsEditing(false);
                  setFormData(formState);
                }}
                disabled={submitting}
                tooltip="Cancel changes"
              />
              <ActionButton
                label={submitting ? 'Saving…' : 'Save Details'}
                intent="primary"
                onClick={() => setShowConfirmModal(true)}
                loading={submitting}
                disabled={
                  submitting || (showSubplatforms && !formState.sub_platforms?.length)
                }
                tooltip="Save changes"
              />
            </Stack>
          ) : (
            <ActionButton
              label="Edit Details"
              intent="secondary"
              onClick={() => setIsEditing(true)}
              tooltip="Edit"
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default ExecutionScope;